/**
 * Active-project selection — the client half of multi-project scoping.
 *
 * The server resolves a caller's project from their `project_users`
 * memberships: it honours an `X-Project-Id` header when the caller is a member
 * of that project, and otherwise falls back to the canonical-first membership.
 * This module is what remembers the PM's pick between page loads and injects
 * the header; `api.ts` is the only consumer of `activeProjectHeader()`.
 *
 * Storage sits next to the session token in localStorage (same key prefix,
 * same lifetime expectations) — see AuthContext's SESSION_STORAGE_KEY.
 *
 * Every accessor is guarded for a missing Storage: the web Vitest project runs
 * in the `node` environment, where `localStorage` / `sessionStorage` / `window`
 * do not exist, and api.ts is imported there.
 */

export const ACTIVE_PROJECT_STORAGE_KEY = 'coffman-tm:project';

/**
 * One-shot latch for the stale-selection recovery reload. Session-scoped so a
 * new tab starts clean; see `handleProjectForbidden`.
 */
const RELOAD_GUARD_KEY = 'coffman-tm:project-reload';

function localStore(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Safari private mode / blocked storage throws on access.
    return null;
  }
}

function sessionStore(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** The PM's stored pick, or null to let the server choose its default. */
export function getActiveProjectId(): string | null {
  const raw = localStore()?.getItem(ACTIVE_PROJECT_STORAGE_KEY) ?? null;
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}

export function setActiveProjectId(projectId: string): void {
  localStore()?.setItem(ACTIVE_PROJECT_STORAGE_KEY, projectId);
}

export function clearActiveProjectId(): void {
  localStore()?.removeItem(ACTIVE_PROJECT_STORAGE_KEY);
}

/**
 * Header fragment to spread into a fetch's headers. Empty when there is no
 * stored pick — sending no header is meaningfully different from sending an
 * empty one: it tells the server to apply its own default.
 */
export function activeProjectHeader(): Record<string, string> {
  const id = getActiveProjectId();
  return id ? { [ACTIVE_PROJECT_HEADER]: id } : {};
}

/**
 * THE header name, exported so nothing can spell it a second way.
 *
 * Headers are a plain `Record<string, string>` until `fetch` normalizes them,
 * and a plain object is CASE-SENSITIVE: writing `'X-Project-Id'` next to this
 * module's `'x-project-id'` produces two properties, which `Headers` then folds
 * into ONE comma-joined value (`"idA, idB"`). The server reads that as a single
 * unknown project id and answers `403 project_forbidden` — indistinguishable
 * from a real membership failure, because those two cases collapse to one
 * answer on purpose.
 *
 * That is not hypothetical: it is exactly what the cross-project transfer
 * dialog's per-call project override did on 2026-08-03 (a member of both jobs
 * saw an empty PCO list and a bare `project_forbidden`). One constant, so an
 * override REPLACES the session's pick instead of appending to it.
 */
export const ACTIVE_PROJECT_HEADER = 'x-project-id';

/**
 * Clear the one-shot reload latch. Called after a successful `/me`, so a
 * *later* stale selection in the same tab can still self-heal.
 */
export function clearProjectReloadGuard(): void {
  sessionStore()?.removeItem(RELOAD_GUARD_KEY);
}

/**
 * Recovery for a stored project the server no longer accepts (membership
 * revoked, project deleted, id copied from another account's browser). Drop
 * the selection and reload once so every in-flight screen re-fetches against
 * the server's default project rather than half the UI showing one project's
 * data and half showing an error.
 *
 * Two independent loop guards, because a reload loop is unrecoverable without
 * devtools:
 *  1. The selection is cleared BEFORE reloading, so the next page load sends no
 *     header at all and structurally cannot 403 `project_forbidden` again.
 *  2. A session-scoped latch, cleared only by a successful `/me`.
 *
 * Returns true when it cleared the selection AND latched a reload — false when
 * there was nothing stored, or when the latch was already spent.
 */
export function handleProjectForbidden(): boolean {
  if (getActiveProjectId() === null) return false;
  clearActiveProjectId();

  const store = sessionStore();
  if (store?.getItem(RELOAD_GUARD_KEY)) return false;
  store?.setItem(RELOAD_GUARD_KEY, '1');

  if (typeof window !== 'undefined') {
    try {
      window.location.assign('/');
    } catch {
      // Non-navigable environment (test harness, embedded view). The selection
      // is already cleared, which is the part that matters.
    }
  }
  return true;
}
